import { useCallback, useEffect, useState } from "react";
import roles from "../../../utils/constants/roles";
import logger from "../../../utils/logger";
import { useImpersonation } from "../../../contexts/ImpersonationContext";

export default function useManagerMode({
  auth,
  service,
  searchParams,
  updateSearchParams,
  loadScorecardHistory,
  setScorecardHistory,
  resetViewState,
}) {
  const { impersonatedUser, impersonationRoles, isImpersonationReady } = useImpersonation();
  // When impersonating, use the impersonated user's derived roles so that
  // sections like My Staff appear as if running as that user. Otherwise fall
  // back to the signed-in user's actual roles.
  const isManager = impersonatedUser
    ? impersonationRoles.includes(roles.ROLE_MANAGER)
    : auth.isInAnyRole([roles.ROLE_MANAGER]);
  const [subordinates, setSubordinates] = useState([]);
  const [isSubordinatesLoading, setIsSubordinatesLoading] = useState(false);
  const [selectedStaffEmail, setSelectedStaffEmail] = useState(() => searchParams.get("staff"));
  const managerViewMode = isManager ? (searchParams.get("tab") ?? "my-team") : null;
  const managerOwnEmail = isManager ? service.getManagerEmail() : null;
  const currentUserEmail = auth.getUsername();

  const loadSubordinates = useCallback(async () => {
    setIsSubordinatesLoading(true);

    try {
      const response = await service.loadSubordinates();
      setSubordinates(Array.isArray(response) ? response : []);
    } catch {
      setSubordinates([]);
      logger.error("Unable to load subordinates.");
    } finally {
      setIsSubordinatesLoading(false);
    }
  }, [service]);

  const handleStaffSelection = useCallback(async (email) => {
    setSelectedStaffEmail(email);
    resetViewState();
    updateSearchParams(email ? { staff: email } : {}, { replace: true });

    if (email) {
      await loadScorecardHistory(email);
    } else {
      setScorecardHistory([]);
    }
  }, [loadScorecardHistory, updateSearchParams, resetViewState, setScorecardHistory]);

  const switchManagerView = useCallback((tab) => {
    resetViewState({ clearHistory: true, clearLoadState: true });

    setSelectedStaffEmail((currentStaff) => {
      const params = { tab };
      if (currentStaff) {
        params.staff = currentStaff;
      }
      updateSearchParams(params, { replace: true });

      if (tab === "my-scorecard") {
        loadScorecardHistory(managerOwnEmail);
      } else if (tab === "my-team" && currentStaff) {
        loadScorecardHistory(currentStaff);
      }

      return currentStaff;
    });
  }, [loadScorecardHistory, updateSearchParams, managerOwnEmail, resetViewState]);

  useEffect(() => {
    // Wait until impersonation is resolved from the URL so history loads for
    // the impersonated user, not the signed-in user.
    if (!isImpersonationReady) return;

    if (isManager) {
      if (managerViewMode === "my-scorecard") {
        loadScorecardHistory(managerOwnEmail);
      } else {
        loadSubordinates();
        const staffFromUrl = searchParams.get("staff");
        if (staffFromUrl) {
          loadScorecardHistory(staffFromUrl);
        }
      }
    } else {
      loadScorecardHistory();
    }
    return () => service.clear();
  }, [isImpersonationReady, isManager, managerViewMode, loadSubordinates, loadScorecardHistory, service]);

  return {
    isManager,
    managerViewMode,
    subordinates,
    setSubordinates,
    isSubordinatesLoading,
    selectedStaffEmail,
    managerOwnEmail,
    currentUserEmail,
    handleStaffSelection,
    switchManagerView,
  };
}
